import { memo, useState } from "react";

import { classNames, Mods } from "shared/lib/classNames/classNames";

import cls from "./SociometryResultItem.module.scss";
import { PupilResult } from "shared/types/sociometry";
import {
    getSociometryDescription,
    getSociometryRole,
    getSociometryRoleColors,
    getSociometryTitle,
} from "../lib/sociometryRoles";
import { Popup } from "shared/ui/Popup";
import { HStack, VStack } from "components/landingNew/shared/ui/Stack";
import { Icon } from "components/landingNew/shared/ui/Icon";
import { ReactComponent as ArrowDown } from "static/img/ic-arrow-down.svg";
import { useAnimatedMount } from "shared/lib/hooks/useAnimatedMount/useAnimatedMount";
import { useCollapse } from "shared/lib/hooks/useCollapse/useCollapse";
import { SociometryResultItemInfo } from "../SociometryResultItemInfo/SociometryResultItemInfo";

interface SociometryResultItemProps {
    className?: string;
    starsIds: Set<string>;
    score: PupilResult;
    notPassedTest?: boolean;
    sessionId: string;
    onAnnulled?: () => void;
}

export const SociometryResultItem = memo((props: SociometryResultItemProps) => {
    const { className, score, starsIds, notPassedTest, sessionId, onAnnulled } = props;

    const role = getSociometryRole(score, starsIds);

    const background = getSociometryRoleColors(role);
    const noBackground = background === "";

    const title = getSociometryTitle(role);
    const description = getSociometryDescription(role);

    const [expanded, setExpanded] = useState(false);

    const [shouldRender, isActive] = useAnimatedMount(expanded, 400);

    const { ref, style } = useCollapse(isActive, 400);

    const showInfoHandler = () => {
        if (notPassedTest) return;
        setExpanded((prev) => !prev);
    };
    const mods: Mods = {
        [cls.expanded]: expanded,
        [cls.notPassedTest]: notPassedTest,
    };
    return (
        <VStack className={classNames(cls.sociometryResultItem, mods, [])}>
            <Popup key={score.pupil?._id} dangerouslySetInnerHTML={description} background={background}>
                <div className={classNames(cls.row, mods, [className])} title={title} onClick={showInfoHandler}>
                    <HStack align="center" maxHeight>
                        <div
                            className={classNames(cls.badge, { [cls.noBackground]: noBackground })}
                            style={{ backgroundColor: background, color: role === "ignored" ? "#000" : "#fff" }}
                        >
                            {title}
                        </div>
                    </HStack>
                    <div className={cls.pupilName}>
                        {`${score.pupil?.surname} ${score.pupil?.name} ${score.pupil?.patronymic || ""}`.trim()}
                    </div>

                    <div className={classNames(cls.center, {}, [cls.score])}>{score.positiveScore}</div>
                    <div className={classNames(cls.center, {}, [cls.score])}>{score.negativeScore}</div>
                    <div className={classNames(cls.center, {}, [cls.score])}>{score.totalScore}</div>

                    {!notPassedTest && (
                        <div className={classNames(cls.expandButton, { [cls.expanded]: expanded }, [])}>
                            <Icon Svg={ArrowDown} className={cls.icon} height={4} width={7} />
                        </div>
                    )}
                </div>
            </Popup>
            {shouldRender && (
                <SociometryResultItemInfo
                    ref={ref}
                    style={style}
                    className={classNames(cls.info, { [cls.expanded]: expanded }, [])}
                    score={score}
                    sessionId={sessionId}
                    onAnnulled={onAnnulled}
                />
            )}
        </VStack>
    );
});

SociometryResultItem.displayName = "SociometryResultItem";
